import {
  Search,
  ShieldCheck,
  FileText,
  MessagesSquare,
  CheckCircle2,
  AppWindow,
  Fingerprint,
  Database,
  Server,
  Plug,
} from "lucide-react";

export const securityAuditMeta = {
  path: "/services/security-audits",
  category: "Security & Compliance",
  title: "Security Audit Services",
  shortTitle: "Security Audits",
  heading: "Security\nAudits.",
  description:
    "Independent, fixed-tier security audits of the codebases you're building, shipping, or acquiring. You get a known price per target up front, a clear lifecycle, and a prioritized report you can act on, not an open-ended hourly bill.",
  metaDescription:
    "Fixed-tier security audits ($5K–$100K) with a 72-hour discovery, prioritized findings report, and 2-week review window. Known price per target from Maxiom Apps.",
};

/** How the engagement works: three pillars + the concurrency cap. */
export const engagementPillars = [
  {
    title: "Scope",
    desc: "Ongoing security audits of the codebases you're building, shipping, or acquiring. You decide what to submit; we confirm what we take on.",
  },
  {
    title: "Pricing",
    desc: "Retainer-friendly and tier-priced. A fixed cost per target, set by technical complexity rather than hours burned.",
  },
  {
    title: "Control",
    desc: "You choose what to submit and when. Every target is scoped, tiered, and accepted before any audit work begins.",
  },
];

export const concurrencyCap = {
  value: "Max 3",
  label: "concurrent audits",
  note: "A hard cap that protects audit depth. We never spread the team too thin.",
};

/** Coverage: the five architectural layers we audit. */
export const coverageIntro = {
  label: "Coverage",
  heading: "We audit every layer of your stack.",
  description:
    "Independent security audits across your entire architecture. Every layer of a system has its own failure modes, and an attacker needs just one weak layer. So we audit all five, end to end, rather than spot-checking the parts that are easy to test.",
  whyItMatters:
    "An audit is only as good as its coverage. One overlooked layer is all it takes, so each engagement maps your system across application, identity, data, infrastructure, and integrations.",
};

export const auditLayers = [
  {
    number: 1,
    icon: AppWindow,
    title: "Application & API Layer",
    scope: "Everything a client or attacker can reach (web, mobile, and endpoints).",
    checks: [
      { name: "Injection", detail: "SQL / NoSQL, command, template, and header injection." },
      { name: "Cross-site scripting", detail: "Reflected, stored, and DOM XSS; input validation and output encoding." },
      { name: "Broken authorization", detail: "IDOR, BOLA / BFLA, and mass assignment." },
      { name: "Business logic", detail: "Workflow bypass, replay, race conditions, and tampering." },
      { name: "API abuse & SSRF", detail: "Rate limits, verb tampering, GraphQL, uploads, and path traversal." },
      { name: "Client & mobile", detail: "CSP, JS supply chain, local secrets, cert pinning, and deep links." },
    ],
  },
  {
    number: 2,
    icon: Fingerprint,
    title: "Identity & Access",
    scope: "Who can authenticate, and what they're allowed to do.",
    checks: [
      { name: "Authentication", detail: "Password policy, MFA, brute-force and credential stuffing, lockout." },
      { name: "Sessions & tokens", detail: "Expiry, rotation, revocation, and JWT validation." },
      { name: "OAuth / OIDC / SSO", detail: "Redirect-URI, state / PKCE, token leakage, and scopes." },
      { name: "Authorization model", detail: "RBAC / ABAC; horizontal and vertical privilege escalation." },
      { name: "Multi-tenancy", detail: "Tenant isolation, cross-tenant access, and RLS enforcement." },
      { name: "Reset & lifecycle", detail: "Token entropy, host-header poisoning, enumeration, and deprovisioning." },
    ],
  },
  {
    number: 3,
    icon: Database,
    title: "Data & Storage",
    scope: "How sensitive data is protected, stored, and exposed.",
    checks: [
      { name: "Classification", detail: "Handling of PII, financial, and secret data." },
      { name: "Encryption", detail: "At rest and in transit; TLS, cipher suites, and cert validation." },
      { name: "Database", detail: "Least privilege, parameterization, RLS, and exposed instances." },
      { name: "Key management", detail: "Storage, rotation, KMS use, and hardcoded keys / seeds." },
      { name: "Leakage", detail: "Logs, backups, error messages, analytics, and third parties." },
      { name: "Lifecycle", detail: "Bucket permissions, retention, deletion, and right-to-erasure." },
    ],
  },
  {
    number: 4,
    icon: Server,
    title: "Infrastructure & Cloud",
    scope: "The environment it all runs on (IAM, network, and configuration).",
    checks: [
      { name: "IAM", detail: "Over-privileged roles, wildcard policies, admin MFA, and rotation." },
      { name: "Network exposure", detail: "Open ports, security groups, and public / management interfaces." },
      { name: "Cloud config", detail: "Default-deny posture, bucket permissions, and logging enabled." },
      { name: "Secrets management", detail: "Env vars, secret stores, and leakage in images / repos." },
      { name: "Containers", detail: "Image provenance, privileged containers, and runtime config." },
      { name: "Monitoring & hardening", detail: "Patch baseline, unused services, and security alerting." },
    ],
  },
  {
    number: 5,
    icon: Plug,
    title: "Integrations & Supply Chain",
    scope: "Everything you depend on and connect to.",
    checks: [
      { name: "APIs & webhooks", detail: "Authentication, signature verification, and replay protection." },
      { name: "Dependencies", detail: "Known CVEs, unmaintained packages, transitive risk, and SBOM." },
      { name: "CI / CD", detail: "Secret handling, build integrity, artifact signing, and least-privilege runners." },
      { name: "Source control", detail: "Branch protection, secrets in history, and access controls." },
      { name: "Callbacks & SSRF", detail: "Webhook validation, idempotency, and server-side request forgery." },
      { name: "Package & egress", detail: "Typosquatting, dependency confusion, and data egress." },
    ],
  },
];

/** Why fixed-tier, not hourly. */
export const pricingModel = {
  hourly: {
    label: "Hourly",
    points: [
      "Rewards us for being slow",
      "No firm number until the end",
      "Built-in incentive to inflate hours",
    ],
  },
  fixedTier: {
    label: "Fixed-tier · our model",
    points: [
      "Rewards accuracy and speed",
      "A known price per target, up front",
      "Misjudge the effort? We absorb it, not you",
    ],
  },
};

/** The five-phase audit lifecycle. */
export const lifecycle = [
  {
    step: 1,
    icon: Search,
    title: "Discovery",
    timing: "72 hours",
    desc: "On code access, we determine the Audit Tier so you know the price before committing.",
  },
  {
    step: 2,
    icon: ShieldCheck,
    title: "Audit",
    timing: "At agreed tier",
    desc: "Runs at the agreed tier, plus any optional priority fee for acceleration.",
  },
  {
    step: 3,
    icon: FileText,
    title: "Preliminary report",
    timing: "On completion",
    desc: "Findings delivered on completion (severity, impact, evidence, and fixes).",
  },
  {
    step: 4,
    icon: MessagesSquare,
    title: "Review",
    timing: "2 weeks",
    desc: "Questions, clarifications, and walkthroughs, included for two weeks.",
  },
  {
    step: 5,
    icon: CheckCircle2,
    title: "Closeout",
    timing: "Mutual sign-off",
    desc: "Mutual sign-off and a Statement of Completion for the work performed.",
  },
];

/** Five fixed audit tiers. */
export const tiers = [
  { tier: "Tier 1", price: "$5K", summary: "Static / scripts" },
  { tier: "Tier 2", price: "$10K", summary: "Standard app" },
  { tier: "Tier 3", price: "$20K", summary: "App · PII · payments" },
  { tier: "Tier 4", price: "$50K", summary: "Multi-tenant / native" },
  { tier: "Tier 5", price: "$100K", summary: "Money-critical" },
];

export const tierFloorNote =
  "Tier = the highest single component, not the average. Any Bitcoin surface floors a project at Tier 2.";

/** Tier-assignment matrix. Columns map to tiers T1–T5. */
export const tierMatrix = {
  columns: ["T1 · $5K", "T2 · $10K", "T3 · $20K", "T4 · $50K", "T5 · $100K"],
  rows: [
    {
      component: "Money movement",
      values: ["None", "Read-only", "3rd-party pay", "Multi-flow payouts", "Custom BTC / keys"],
    },
    {
      component: "Auth & authz",
      values: ["None", "Single-tenant", "Auth + roles", "Multi-tenant", "+ financial"],
    },
    {
      component: "Exposure",
      values: ["Internal", "Gated", "Public, gated", "Public + mobile", "+ real money"],
    },
    {
      component: "Codebase scope",
      values: ["Script", "Small app", "Full app", "Monorepo", "+ native"],
    },
    {
      component: "Sensitive data",
      values: ["Low", "Internal ops", "PII", "+ financial", "+ keys"],
    },
    {
      component: "Stack risk",
      values: ["Static", "Standard web", "+ pay SDKs", "Native / PHP", "Custom crypto"],
    },
    {
      component: "Bespoke security",
      values: ["None", "Minimal", "Some custom", "Integrations", "Wallet / signing"],
    },
  ],
};

/** Discovery deep-dive outcomes. */
export const discoveryOutcomes = [
  {
    title: "Tier assigned",
    desc: "You accept → 50% of the audit fee is due and the audit begins. Decline → you walk at no cost.",
  },
  {
    title: "Won't do",
    desc: "No charge. Project-side reasons come with a brief, written no-go memo explaining why.",
  },
];

/** End-to-end example (retainer wording removed). */
export const exampleSteps = [
  { label: "Discovery assigns Tier 3", value: "$20,000 audit fee" },
  { label: "You agree → 50% due", value: "$10,000 to begin" },
  { label: "Delivered 25% faster", value: "$5,000 priority fee" },
];
export const exampleTotal = "$25,000";
export const exampleNote =
  "Balance due within 2 weeks of completion. Variant: a Tier 4 ($50K) target makes the 50% $25,000, decline and walk at no cost.";

/** Preliminary report contents. */
export const reportContents = [
  "Executive summary & risk posture",
  "Scope, version/commit & methodology",
  "Findings: severity, impact, evidence, fix",
  "Severity model + risk summary",
  "Limitations & out-of-scope items",
  "Prioritized remediation guidance",
];

/** Payment schedule (master retainer row intentionally omitted). */
export const paymentSchedule = [
  {
    milestone: "Audit fee (50%)",
    timing: "After discovery; audit begins when paid",
    amount: "50% of tier",
  },
  {
    milestone: "Balance + priority",
    timing: "Within 2 weeks of completion",
    amount: "Remaining 50% + priority",
  },
  {
    milestone: "Extended review",
    timing: "Each week beyond the included 2",
    amount: "$5,000 / week",
  },
  {
    milestone: "Additional audit",
    timing: "50% up front / 50% on delivery",
    amount: "Fixed fee (quoted)",
  },
];

/** Terms at a glance (retainer + validity + client-specific MSA framing removed). */
export const termsAtAGlance = [
  { label: "Currency", desc: "All figures in USD." },
  { label: "Pricing", desc: "Fixed per target, set by technical complexity, known up front." },
  {
    label: "Liability",
    desc: "Capped at fees paid for that audit; consequential damages waived.",
  },
  { label: "Capacity", desc: "No more than 3 audits run concurrently." },
];

export const securityAuditFaqs = [
  {
    q: "What does a security audit cover?",
    a: "We review the codebase you submit for security weaknesses across money movement, authentication and authorization, data exposure, sensitive-data handling, stack risk, and any bespoke security logic. You receive a prioritized report with severity, impact, evidence, and concrete fixes for each finding.",
  },
  {
    q: "How is the price determined?",
    a: "Pricing is fixed per target and set by technical complexity, not hours. During a 72-hour discovery phase we assign one of five tiers ($5K, $10K, $20K, $50K, $100K). The tier equals the highest single complexity component (not the average) and any Bitcoin surface floors a project at Tier 2.",
  },
  {
    q: "Why fixed-tier pricing instead of hourly?",
    a: "Hourly billing rewards slowness, hides the final number until the end, and creates an incentive to inflate hours. Fixed-tier pricing gives you a known price per target up front and rewards accuracy and speed. If we misjudge the effort, we absorb it, not you.",
  },
  {
    q: "What happens during discovery?",
    a: "Within 72 hours of getting code access we determine the Audit Tier. If you accept, 50% of the audit fee is due and the audit begins. If you decline, you walk away at no cost. If we decide not to take it on, there's no charge and you get a brief written no-go memo.",
  },
  {
    q: "Can I get an audit done faster?",
    a: "Yes. An optional priority fee accelerates an audit by up to 25%. The fee is proportional to the acceleration you choose and is applied to the audit's base (tier) cost, for example, a Tier 2 ($10K) target delivered 25% faster adds $2,500.",
  },
  {
    q: "What's included after the report is delivered?",
    a: "A two-week review window is included for questions, clarifications, and walkthroughs. It does not cover new audit work or fix verification. Beyond the included two weeks, extended review is $5,000 per week.",
  },
  {
    q: "What do I get at closeout?",
    a: "The project closes on mutual agreement and we issue a Statement of Completion confirming the audit was performed on a given date. It attests to the work performed, it is not a warranty that the system is secure.",
  },
  {
    q: "Can you re-audit after we fix the findings?",
    a: "Yes. A re-audit to verify fixes, or an expanded scope, is quoted as a fixed fee and follows the same model as a standard audit, 50% up front and 50% on delivery.",
  },
];
